import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { CONTINENT_COORDS } from '../data/continentCoords';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Point-in-polygon check using ray casting
const pointInPolygon = (lat, lng, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i];
    const [yj, xj] = polygon[j];
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

// Generate dots using high-quality polygon data
const generateLandDots = () => {
  const dots = [];
  const resolution = 1.2; // Higher resolution = more dots = denser continents
  
  for (let lat = -70; lat <= 85; lat += resolution) {
    // Adjust longitude step based on latitude for better distribution near poles
    const lngStep = resolution / Math.cos((lat * Math.PI) / 180);
    const adjustedLngStep = Math.min(lngStep, resolution * 2);
    
    for (let lng = -180; lng <= 180; lng += adjustedLngStep) {
      let isLand = false;
      
      for (const [, polygon] of Object.entries(CONTINENT_COORDS)) {
        if (pointInPolygon(lat, lng, polygon)) {
          isLand = true;
          break;
        }
      }
      
      if (isLand) {
        // Add slight organic jitter for natural look
        const jLat = lat + (Math.random() - 0.5) * resolution * 0.25;
        const jLng = lng + (Math.random() - 0.5) * resolution * 0.25;
        dots.push([jLat, jLng]);
      }
    }
  }
  
  return dots;
};

const WORLD_DOTS = generateLandDots();

const CobeGlobe = ({ partners, onPinClick, onAddPartner, isAdmin }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const rotationRef = useRef(0);
  const isDraggingRef = useRef(false);
  const lastXRef = useRef(0);
  const velocityRef = useRef(0);
  
  const locations = {
    'Brasil': { lat: -14.235, lng: -51.925 },
    'USA': { lat: 37.090, lng: -95.712 },
    'Dubai': { lat: 25.276, lng: 55.296 },
  };

  const project = useCallback((lat, lng, radius, rotation) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + rotation) * (Math.PI / 180);
    
    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.cos(phi);
    const z = radius * Math.sin(phi) * Math.sin(theta);
    
    // Tilt
    const tilt = 0.4;
    const tiltedY = y * Math.cos(tilt) - z * Math.sin(tilt);
    const tiltedZ = y * Math.sin(tilt) + z * Math.cos(tilt);
    
    return { x, y: tiltedY, z: tiltedZ, visible: tiltedZ > -radius * 0.15 };
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = size * 0.38;
    const rotation = rotationRef.current;
    
    // Clear
    ctx.fillStyle = '#030508';
    ctx.fillRect(0, 0, size, size);
    
    // Globe background with gradient
    const bgGrad = ctx.createRadialGradient(
      center - radius * 0.25, center - radius * 0.3, 0,
      center, center, radius
    );
    bgGrad.addColorStop(0, 'rgba(25, 32, 48, 0.95)');
    bgGrad.addColorStop(0.6, 'rgba(12, 16, 28, 0.98)');
    bgGrad.addColorStop(1, 'rgba(6, 10, 18, 1)');
    
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.fillStyle = bgGrad;
    ctx.fill();
    
    // Glow
    const glow = ctx.createRadialGradient(center, center, radius * 0.9, center, center, radius * 1.15);
    glow.addColorStop(0, 'rgba(60, 130, 200, 0.08)');
    glow.addColorStop(1, 'rgba(60, 130, 200, 0)');
    ctx.beginPath();
    ctx.arc(center, center, radius * 1.15, 0, Math.PI * 2);
    ctx.fillStyle = glow;
    ctx.fill();
    
    // Draw land dots - improved visual style
    WORLD_DOTS.forEach(([lat, lng]) => {
      const pos = project(lat, lng, radius, rotation);
      if (!pos.visible) return;
      
      const screenX = center + pos.x;
      const screenY = center - pos.y;
      const depth = (pos.z + radius) / (2 * radius);
      
      // Smoother dot size and opacity based on depth
      const dotRadius = 0.6 + depth * 0.8;
      const opacity = 0.08 + depth * 0.6;
      
      // Draw dot with subtle glow
      ctx.beginPath();
      ctx.arc(screenX, screenY, dotRadius, 0, Math.PI * 2);
      
      // Use a blue-ish tint for ocean-facing, brighter for front
      const r = 170 + Math.floor(depth * 50);
      const g = 190 + Math.floor(depth * 40);
      const b = 220 + Math.floor(depth * 30);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      ctx.fill();
    });
    
    // Draw partner markers - LARGER SIZE
    Object.entries(locations).forEach(([name, loc]) => {
      const pos = project(loc.lat, loc.lng, radius, rotation);
      if (!pos.visible || pos.z < 0) return;
      
      const screenX = center + pos.x;
      const screenY = center - pos.y;
      const countryPartners = partners.filter(p => p.country === name);
      const depth = (pos.z + radius) / (2 * radius);
      
      // Animated pulse - BIGGER
      const pulse = 28 + Math.sin(Date.now() / 350) * 10;
      
      // Outer pulse
      ctx.beginPath();
      ctx.arc(screenX, screenY, pulse, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(34, 211, 238, ${0.3 * depth})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      
      // Inner ring - BIGGER
      ctx.beginPath();
      ctx.arc(screenX, screenY, 18, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(34, 211, 238, ${0.6 * depth})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      
      // Center glow - BIGGER
      const cGlow = ctx.createRadialGradient(screenX, screenY, 0, screenX, screenY, 26);
      cGlow.addColorStop(0, `rgba(34, 211, 238, ${0.9 * depth})`);
      cGlow.addColorStop(0.4, `rgba(34, 211, 238, ${0.4 * depth})`);
      cGlow.addColorStop(1, 'rgba(34, 211, 238, 0)');
      ctx.beginPath();
      ctx.arc(screenX, screenY, 26, 0, Math.PI * 2);
      ctx.fillStyle = cGlow;
      ctx.fill();
      
      // Center dot - BIGGER
      ctx.beginPath();
      ctx.arc(screenX, screenY, 9, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(34, 211, 238, ${depth})`;
      ctx.fill();
      
      // Count badge - BIGGER
      if (countryPartners.length > 0) {
        ctx.beginPath();
        ctx.arc(screenX + 14, screenY - 16, 12, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(34, 211, 238, 0.95)';
        ctx.fill();
        ctx.fillStyle = '#000';
        ctx.font = 'bold 12px Jost, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(countryPartners.length.toString(), screenX + 14, screenY - 15);
      }
      
      // Label - BIGGER
      ctx.fillStyle = `rgba(255, 255, 255, ${0.7 * depth})`;
      ctx.font = '13px Jost, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(name, screenX, screenY + 28);
    });
  }, [partners, project]);

  // Animation loop
  useEffect(() => {
    const animate = () => {
      if (!isDraggingRef.current) {
        // Apply velocity
        if (Math.abs(velocityRef.current) > 0.001) {
          rotationRef.current += velocityRef.current;
          velocityRef.current *= 0.95;
        } else {
          rotationRef.current += 0.15;
        }
      }
      draw();
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [draw]);

  const handlePointerDown = (e) => {
    isDraggingRef.current = true;
    lastXRef.current = e.clientX || e.touches?.[0]?.clientX || 0;
    velocityRef.current = 0;
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const x = e.clientX || e.touches?.[0]?.clientX || 0;
    const delta = x - lastXRef.current;
    rotationRef.current += delta * 0.35;
    velocityRef.current = delta * 0.35;
    lastXRef.current = x;
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
  };

  const handleClick = (e) => {
    if (Math.abs(velocityRef.current) > 2) return; // Only ignore fast swipes
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;
    
    const center = canvas.width / 2;
    const radius = canvas.width * 0.38;
    
    Object.entries(locations).forEach(([name, loc]) => {
      const pos = project(loc.lat, loc.lng, radius, rotationRef.current);
      if (!pos.visible) return;
      
      const screenX = center + pos.x;
      const screenY = center - pos.y;
      const dist = Math.sqrt((clickX - screenX) ** 2 + (clickY - screenY) ** 2);
      
      if (dist < 55) {
        const countryPartners = partners.filter(p => p.country === name);
        onPinClick(name, countryPartners);
      }
    });
  };

  return (
    <div className="relative w-full max-w-[380px] mx-auto">
      <canvas
        ref={canvasRef}
        width={760}
        height={760}
        className="w-full aspect-square cursor-grab active:cursor-grabbing"
        style={{ background: '#030508' }}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        onClick={handleClick}
      />
      
      {isAdmin && (
        <motion.button
          onClick={onAddPartner}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="absolute bottom-5 right-5 w-14 h-14 rounded-full flex items-center justify-center z-20"
          style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.95) 0%, rgba(37,99,235,0.95) 100%)',
            boxShadow: '0 0 30px rgba(59,130,246,0.5), 0 4px 20px rgba(0,0,0,0.4)'
          }}
          data-testid="add-partner-btn"
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </motion.button>
      )}
    </div>
  );
};

const WPartners = ({ currentMember, isAdmin }) => {
  const [partners, setPartners] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryPartners, setCountryPartners] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractPartner, setContractPartner] = useState(null);
  const [contractHtml, setContractHtml] = useState('');
  const [contractLoading, setContractLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedPartnerId, setSavedPartnerId] = useState(null);
  const [newPartner, setNewPartner] = useState({
    name: '',
    fantasy_name: '',
    cnpj: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    description: '',
    address: '',
    city: '',
    state: '',
    cep: '',
    country: 'Brasil',
    category: 'Restaurante',
    commission_rate: 10,
    discount_rate: 15,
    instagram: '',
    website: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const response = await axios.get(`${API}/api/partners`);
      setPartners(response.data || []);
    } catch (error) {
      setPartners([
        { id: '1', name: 'Fasano', description: 'Restaurante italiano premium', address: 'Rua Vittorio Fasano, 88 - SP', country: 'Brasil', category: 'Restaurante' },
        { id: '2', name: 'Cigar Bar Havana', description: 'Bar de charutos e vinhos', address: 'Av. Paulista, 1000 - SP', country: 'Brasil', category: 'Bar' },
        { id: '3', name: 'The Capital Grille', description: 'Steakhouse premium', address: 'New York, NY', country: 'USA', category: 'Restaurante' },
        { id: '4', name: 'Burj Al Arab', description: 'Hotel & Lounge', address: 'Jumeirah St, Dubai', country: 'Dubai', category: 'Hotel' },
      ]);
    }
    setLoading(false);
  };

  const handlePinClick = (country, partnersList) => {
    setSelectedCountry(country);
    setCountryPartners(partnersList);
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    try {
      // Adaptar campos para o backend
      const partnerData = {
        company_name: newPartner.name,
        fantasy_name: newPartner.fantasy_name || newPartner.name,
        cnpj: newPartner.cnpj,
        contact_name: newPartner.contact_name,
        contact_phone: newPartner.contact_phone,
        contact_email: newPartner.contact_email,
        category: newPartner.category.toLowerCase(),
        address: newPartner.address,
        city: newPartner.city,
        state: newPartner.state,
        cep: newPartner.cep,
        country: newPartner.country || 'Brasil',
        instagram: newPartner.instagram,
        website: newPartner.website,
        commission_rate: parseFloat(newPartner.commission_rate) || 10,
        discount_rate: parseFloat(newPartner.discount_rate) || 15
      };
      const response = await axios.post(`${API}/api/partners`, partnerData);
      const saved = response.data;
      setPartners(prev => [...prev, { ...saved, name: saved.fantasy_name || saved.company_name || saved.name, country: newPartner.country || 'Brasil' }]);
      setShowAddModal(false);
      setSavedPartnerId(saved.id);
      setSaveSuccess(true);
      setTimeout(() => { setSaveSuccess(false); setSavedPartnerId(null); }, 5000);
      
      // Auto-generate contract for the new partner
      try {
        await axios.post(`${API}/api/partners/contracts/generate`, { partner_id: saved.id });
      } catch (e) { console.log('Auto-contract generation:', e); }
    } catch (error) {
      console.error('Error saving partner:', error);
    }
    setNewPartner({ name: '', fantasy_name: '', cnpj: '', contact_name: '', contact_phone: '', contact_email: '', description: '', address: '', city: '', state: '', cep: '', country: 'Brasil', category: 'Restaurante', commission_rate: 10, discount_rate: 15, instagram: '', website: '', documents: [] });
  };

  // Gerar contrato de parceria
  const handleGenerateContract = async (partner) => {
    setContractPartner(partner);
    setContractLoading(true);
    setShowContractModal(true);
    
    try {
      const response = await axios.post(`${API}/api/partners/contracts/generate`, {
        partner_id: partner.id
      });
      if (response.data?.html) {
        setContractHtml(response.data.html);
      }
    } catch (error) {
      console.error('Erro ao gerar contrato:', error);
      alert('Erro ao gerar contrato. Verifique se o parceiro tem CNPJ cadastrado.');
    }
    setContractLoading(false);
  };

  // Download PDF do contrato
  const handleDownloadContractPdf = async () => {
    if (!contractPartner) return;
    
    try {
      const response = await axios.get(`${API}/api/partners/contracts/${contractPartner.contract_id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contrato_Parceria_${contractPartner.fantasy_name || contractPartner.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
    }
  };

  const handleEditPartner = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/api/partners/${editingPartner.id}`, editingPartner);
    } catch (error) {
      console.error('Error updating partner:', error);
    }
    setPartners(prev => prev.map(p => p.id === editingPartner.id ? editingPartner : p));
    if (selectedCountry) {
      setCountryPartners(prev => prev.map(p => p.id === editingPartner.id ? editingPartner : p));
    }
    setEditingPartner(null);
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Tem certeza que deseja excluir este parceiro?')) return;
    try {
      await axios.delete(`${API}/api/partners/${partnerId}`);
    } catch (error) {
      console.error('Error deleting partner:', error);
    }
    setPartners(prev => prev.filter(p => p.id !== partnerId));
    setCountryPartners(prev => prev.filter(p => p.id !== partnerId));
  };

  const categories = ['Restaurante', 'Bar', 'Hotel', 'Vinicola', 'Loja', 'Clube', 'Charutaria', 'Outro'];

  return (
    <div className="space-y-6 pb-20">
      {/* Success Toast with Partner ID */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => { setSaveSuccess(false); setSavedPartnerId(null); }}
          >
            <motion.div
              initial={{ y: 30 }}
              animate={{ y: 0 }}
              className="mx-6 p-6 rounded-3xl text-center"
              style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.1))', border: '1px solid rgba(16,185,129,0.3)', backdropFilter: 'blur(24px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h3 className="text-emerald-400 text-xl font-['Jost'] font-bold mb-1">Parceiro Cadastrado!</h3>
              <p className="text-white/50 text-sm mb-4">Cadastro confirmado e salvo com sucesso</p>
              {savedPartnerId && (
                <div className="px-4 py-3 rounded-xl bg-black/30 border border-emerald-500/20 mb-3">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Partner ID</p>
                  <p className="text-white font-mono font-bold text-lg tracking-wide">{savedPartnerId}</p>
                </div>
              )}
              <p className="text-white/30 text-[10px]">Toque para fechar</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div 
        className="relative rounded-3xl overflow-hidden p-6 backdrop-blur-xl"
        style={{ 
          background: 'linear-gradient(145deg, rgba(10,15,30,0.95) 0%, rgba(5,10,20,0.98) 100%)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <div className="flex items-center gap-4">
          <img src="/w-logo.png" alt="W" className="w-12 h-12 object-contain" />
          <div>
            <h2 className="text-2xl font-bold font-['Jost'] text-white/90">W Partners</h2>
            <p className="text-white/40 text-sm">Parceiros exclusivos ao redor do mundo</p>
          </div>
        </div>
      </div>

      <div 
        className="rounded-3xl overflow-hidden p-4 md:p-6"
        style={{ 
          background: 'linear-gradient(145deg, rgba(3,5,8,0.99) 0%, rgba(2,3,5,1) 100%)',
          border: '1px solid rgba(255,255,255,0.04)'
        }}
      >
        <CobeGlobe 
          partners={partners} 
          onPinClick={handlePinClick}
          onAddPartner={() => setShowAddModal(true)}
          isAdmin={isAdmin}
        />
        <p className="text-center text-white/20 text-xs mt-4 font-['Jost']">
          Arraste para girar o globo - Toque nos pins para ver parceiros
        </p>
      </div>

      <AnimatePresence>
        {selectedCountry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => setSelectedCountry(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-gradient-to-br from-[#0f1419] to-[#0a0d10] rounded-t-3xl overflow-hidden"
              style={{ maxHeight: '70vh' }}
            >
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold font-['Jost'] text-white">{selectedCountry}</h3>
                  <p className="text-white/40 text-sm">{countryPartners.length} parceiro(s)</p>
                </div>
                <button onClick={() => setSelectedCountry(null)} className="p-2 text-white/50 hover:text-white/80" data-testid="close-country-modal">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(70vh - 80px)' }}>
                {countryPartners.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-white/[0.03] rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                    </div>
                    <p className="text-white/40 text-sm">Nenhum parceiro cadastrado</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {countryPartners.map((partner) => (
                      <div key={partner.id} className="bg-white/[0.03] rounded-2xl p-4 border border-white/[0.06]">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-white font-['Jost'] font-medium">{partner.fantasy_name || partner.name}</h4>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">{partner.category}</span>
                            {isAdmin && (
                              <>
                                {/* Botão Gerar Contrato */}
                                <button 
                                  onClick={() => handleGenerateContract(partner)} 
                                  className="p-1.5 text-white/40 hover:text-emerald-400 transition" 
                                  title="Gerar Contrato"
                                  data-testid={`contract-partner-${partner.id}`}
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </button>
                                <button onClick={() => setEditingPartner({...partner})} className="p-1.5 text-white/40 hover:text-blue-400" data-testid={`edit-partner-${partner.id}`}>
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button onClick={() => handleDeletePartner(partner.id)} className="p-1.5 text-white/40 hover:text-red-400" data-testid={`delete-partner-${partner.id}`}>
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        {partner.cnpj && <p className="text-white/40 text-xs mb-1">CNPJ: {partner.cnpj}</p>}
                        <p className="text-white/60 text-sm mb-2">{partner.description}</p>
                        <p className="text-white/30 text-xs flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          {partner.address}
                        </p>
                        {/* Info de desconto para membros */}
                        {partner.discount_rate && (
                          <div className="mt-2 pt-2 border-t border-white/[0.06] flex items-center justify-between">
                            <span className="text-[10px] text-emerald-400/70">Desconto WClub: {partner.discount_rate}%</span>
                            {partner.contract_signed && (
                              <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">Contrato Ativo</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-gradient-to-br from-[#0f1419] to-[#0a0d10] rounded-3xl overflow-hidden border border-white/[0.08] max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b border-white/[0.06] sticky top-0 bg-[#0f1419] z-10">
                <h3 className="text-xl font-bold font-['Jost'] text-white">Cadastrar Parceiro</h3>
                <p className="text-white/40 text-sm">Preencha os dados do estabelecimento parceiro</p>
              </div>
              <form onSubmit={handleAddPartner} className="p-5 space-y-4">
                {/* Dados da Empresa */}
                <div className="border-b border-white/[0.06] pb-4 mb-4">
                  <h4 className="text-blue-400 text-xs font-medium mb-3 uppercase tracking-wider">Dados da Empresa</h4>
                  <div className="space-y-3">
                    <input type="text" value={newPartner.name} onChange={(e) => setNewPartner(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Razão Social" required />
                    <input type="text" value={newPartner.fantasy_name} onChange={(e) => setNewPartner(prev => ({ ...prev, fantasy_name: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Nome Fantasia" />
                    <input type="text" value={newPartner.cnpj} onChange={(e) => setNewPartner(prev => ({ ...prev, cnpj: e.target.value }))} 
                      onBlur={async (e) => {
                        const cnpj = e.target.value.replace(/\D/g, '');
                        if (cnpj.length === 14) {
                          try {
                            const res = await axios.get(`${API}/api/partners/cnpj-lookup?cnpj=${encodeURIComponent(e.target.value)}`);
                            const d = res.data;
                            setNewPartner(prev => ({
                              ...prev,
                              name: d.company_name || prev.name,
                              fantasy_name: d.fantasy_name || prev.fantasy_name,
                              address: d.address || prev.address,
                              city: d.city || prev.city,
                              state: d.state || prev.state,
                              cep: d.cep || prev.cep,
                              contact_phone: d.phone || prev.contact_phone,
                              contact_email: d.email || prev.contact_email,
                            }));
                          } catch (err) { console.log('CNPJ lookup failed:', err); }
                        }
                      }}
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="CNPJ (00.000.000/0000-00)" required />
                  </div>
                </div>

                {/* Contato */}
                <div className="border-b border-white/[0.06] pb-4 mb-4">
                  <h4 className="text-blue-400 text-xs font-medium mb-3 uppercase tracking-wider">Responsável</h4>
                  <div className="space-y-3">
                    <input type="text" value={newPartner.contact_name} onChange={(e) => setNewPartner(prev => ({ ...prev, contact_name: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Nome do Responsável" required />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="tel" value={newPartner.contact_phone} onChange={(e) => setNewPartner(prev => ({ ...prev, contact_phone: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Telefone" required />
                      <input type="email" value={newPartner.contact_email} onChange={(e) => setNewPartner(prev => ({ ...prev, contact_email: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Email" required />
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="border-b border-white/[0.06] pb-4 mb-4">
                  <h4 className="text-blue-400 text-xs font-medium mb-3 uppercase tracking-wider">Endereço</h4>
                  <div className="space-y-3">
                    <input type="text" value={newPartner.address} onChange={(e) => setNewPartner(prev => ({ ...prev, address: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Endereço completo" required />
                    <div className="grid grid-cols-3 gap-3">
                      <input type="text" value={newPartner.city} onChange={(e) => setNewPartner(prev => ({ ...prev, city: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Cidade" />
                      <input type="text" value={newPartner.state} onChange={(e) => setNewPartner(prev => ({ ...prev, state: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="UF" maxLength={2} />
                      <input type="text" value={newPartner.cep} onChange={(e) => setNewPartner(prev => ({ ...prev, cep: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="CEP" />
                    </div>
                    <select value={newPartner.country} onChange={(e) => setNewPartner(prev => ({ ...prev, country: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none">
                      <option value="Brasil">Brasil</option>
                      <option value="USA">USA</option>
                      <option value="Dubai">Dubai</option>
                    </select>
                  </div>
                </div>

                {/* Categoria e Comercial */}
                <div className="border-b border-white/[0.06] pb-4 mb-4">
                  <h4 className="text-blue-400 text-xs font-medium mb-3 uppercase tracking-wider">Comercial</h4>
                  <div className="space-y-3">
                    <select value={newPartner.category} onChange={(e) => setNewPartner(prev => ({ ...prev, category: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none">
                      {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                    </select>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-white/50 text-[10px] mb-1 block">Comissão WClub (%)</label>
                        <input type="number" value={newPartner.commission_rate} onChange={(e) => setNewPartner(prev => ({ ...prev, commission_rate: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none focus:border-blue-500/50" min="0" max="50" />
                      </div>
                      <div>
                        <label className="text-white/50 text-[10px] mb-1 block">Desconto Membros (%)</label>
                        <input type="number" value={newPartner.discount_rate} onChange={(e) => setNewPartner(prev => ({ ...prev, discount_rate: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none focus:border-blue-500/50" min="0" max="50" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Redes */}
                <div className="border-b border-white/[0.06] pb-4 mb-4">
                  <h4 className="text-blue-400 text-xs font-medium mb-3 uppercase tracking-wider">Redes Sociais</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" value={newPartner.instagram} onChange={(e) => setNewPartner(prev => ({ ...prev, instagram: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="@instagram" />
                    <input type="url" value={newPartner.website} onChange={(e) => setNewPartner(prev => ({ ...prev, website: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder:text-white/30 outline-none focus:border-blue-500/50" placeholder="Website" />
                  </div>
                </div>

                {/* Documentos */}
                <div className="mb-4">
                  <h4 className="text-blue-400 text-xs font-medium mb-3 uppercase tracking-wider">Documentos</h4>
                  <p className="text-white/30 text-[10px] mb-3">Anexe documentos do estabelecimento (CNPJ, alvará, contrato social)</p>
                  <div className="flex gap-2 mb-3">
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/50 text-xs cursor-pointer hover:bg-white/[0.06] transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" /></svg>
                      Camera
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => setNewPartner(prev => ({ ...prev, documents: [...(prev.documents || []), { name: file.name, data: ev.target.result, type: 'camera' }] }));
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }} />
                    </label>
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/50 text-xs cursor-pointer hover:bg-white/[0.06] transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                      Arquivo
                      <input type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => setNewPartner(prev => ({ ...prev, documents: [...(prev.documents || []), { name: file.name, data: ev.target.result, type: 'file' }] }));
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }} />
                    </label>
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/50 text-xs cursor-pointer hover:bg-white/[0.06] transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" /></svg>
                      Scanner
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => setNewPartner(prev => ({ ...prev, documents: [...(prev.documents || []), { name: file.name, data: ev.target.result, type: 'scanner' }] }));
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }} />
                    </label>
                  </div>
                  {/* Document previews */}
                  {newPartner.documents?.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {newPartner.documents.map((doc, idx) => (
                        <div key={idx} className="relative group">
                          {doc.data.startsWith('data:image') ? (
                            <img src={doc.data} alt={doc.name} className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                            </div>
                          )}
                          <button type="button" onClick={() => setNewPartner(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }))}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                          <p className="text-[8px] text-white/30 truncate w-16 mt-0.5">{doc.type}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 py-3 bg-white/[0.06] hover:bg-white/[0.1] rounded-xl text-white/60 font-['Jost']">Cancelar</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl font-['Jost'] text-white" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.9) 0%, rgba(37,99,235,0.9) 100%)' }} data-testid="submit-add-partner">Cadastrar</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editingPartner && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditingPartner(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-gradient-to-br from-[#0f1419] to-[#0a0d10] rounded-3xl overflow-hidden border border-white/[0.08]">
              <div className="p-5 border-b border-white/[0.06]">
                <h3 className="text-xl font-bold font-['Jost'] text-white">Editar Parceiro</h3>
              </div>
              <form onSubmit={handleEditPartner} className="p-5 space-y-4">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Nome</label>
                  <input type="text" value={editingPartner.name} onChange={(e) => setEditingPartner(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none" required />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Descricao</label>
                  <input type="text" value={editingPartner.description || ''} onChange={(e) => setEditingPartner(prev => ({ ...prev, description: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Endereco</label>
                  <input type="text" value={editingPartner.address} onChange={(e) => setEditingPartner(prev => ({ ...prev, address: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Pais</label>
                    <select value={editingPartner.country} onChange={(e) => setEditingPartner(prev => ({ ...prev, country: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none">
                      <option value="Brasil">Brasil</option>
                      <option value="USA">USA</option>
                      <option value="Dubai">Dubai</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Categoria</label>
                    <select value={editingPartner.category} onChange={(e) => setEditingPartner(prev => ({ ...prev, category: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white outline-none">
                      {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setEditingPartner(null)} className="flex-1 py-3 bg-white/[0.06] hover:bg-white/[0.1] rounded-xl text-white/60 font-['Jost']">Cancelar</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl font-['Jost'] text-white" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.9) 0%, rgba(37,99,235,0.9) 100%)' }} data-testid="submit-edit-partner">Salvar</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de Contrato de Parceria */}
      <AnimatePresence>
        {showContractModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" 
            onClick={() => setShowContractModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }} 
              onClick={(e) => e.stopPropagation()} 
              className="w-full max-w-4xl bg-gradient-to-br from-[#0f1419] to-[#0a0d10] rounded-3xl overflow-hidden border border-blue-500/20"
              style={{ maxHeight: '90vh' }}
            >
              {/* Header */}
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#0f1419] z-10">
                <div>
                  <h3 className="text-xl font-bold font-['Jost'] text-white">Contrato de Parceria</h3>
                  <p className="text-white/40 text-sm">{contractPartner?.fantasy_name || contractPartner?.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Download PDF */}
                  <button 
                    onClick={handleDownloadContractPdf}
                    className="px-4 py-2 rounded-xl text-sm flex items-center gap-2 text-emerald-400"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.05) 100%)',
                      border: '1px solid rgba(16,185,129,0.3)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    PDF
                  </button>
                  {/* Imprimir */}
                  <button 
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(contractHtml);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="px-4 py-2 rounded-xl text-sm flex items-center gap-2 text-blue-400"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(59,130,246,0.05) 100%)',
                      border: '1px solid rgba(59,130,246,0.3)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Imprimir
                  </button>
                  {/* Fechar */}
                  <button 
                    onClick={() => setShowContractModal(false)} 
                    className="p-2 text-white/40 hover:text-white/80 transition rounded-lg hover:bg-white/[0.05]"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Contract Content */}
              <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
                {contractLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : contractHtml ? (
                  <iframe
                    srcDoc={contractHtml}
                    title="Contrato de Parceria"
                    className="w-full h-[70vh] bg-white rounded-xl"
                    style={{ minHeight: '600px' }}
                  />
                ) : (
                  <div className="text-center py-20 text-white/40">
                    <p>Erro ao carregar contrato</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WPartners;
